import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { UserRole, getDefaultRouteForRole } from './roles';
import { hasRoutePermission } from './routes';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  redirectTo?: string;
}

/**
 * RoleBasedRoute Component
 * Provides role-based access control for routes
 *
 * Features:
 * - Checks if user is authenticated
 * - Checks if user has permission based on their role
 * - Can use explicit allowedRoles prop OR route config permissions
 * - Redirects unauthorized users to their default route
 *
 * Usage:
 * <RoleBasedRoute allowedRoles={[UserRole.ADMIN]}>
 *   <AdminDashboard />
 * </RoleBasedRoute>
 *
 * If allowedRoles is not provided, it uses the RoutePermissions config from routes.ts
 */
const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  children,
  allowedRoles,
  redirectTo
}) => {
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();

  console.log('🔐 RoleBasedRoute: Checking access for path:', location.pathname);
  console.log('🔐 RoleBasedRoute: User role:', user?.role);
  console.log('🔐 RoleBasedRoute: Allowed roles:', allowedRoles);

  // Check localStorage directly for immediate authentication check
  const userJson = localStorage.getItem('mile-lxp-user');
  const isAuthenticatedFlag = localStorage.getItem('mile-lxp-authenticated');
  const hasAuthData = userJson && isAuthenticatedFlag === 'true';

  // Not authenticated - redirect to login
  if (!hasAuthData && !isAuthenticated) {
    console.log('🔐 RoleBasedRoute: User not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Get user from localStorage if not in store
  const currentUser = user || (userJson ? JSON.parse(userJson) : null);
  const userRole = currentUser?.role as UserRole;

  console.log('🔐 RoleBasedRoute: Current user role:', userRole);

  // No user role found
  if (!userRole) {
    console.log('🔐 RoleBasedRoute: No user role found, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions
  let hasPermission = false;

  if (allowedRoles && allowedRoles.length > 0) {
    // Use explicitly provided allowed roles
    hasPermission = allowedRoles.includes(userRole);
    console.log('🔐 RoleBasedRoute: Using explicit allowedRoles, permission:', hasPermission);
  } else {
    // Use RoutePermissions config from routes.ts
    hasPermission = hasRoutePermission(userRole, location.pathname);
    console.log('🔐 RoleBasedRoute: Using RoutePermissions config, permission:', hasPermission);
  }

  // User doesn't have permission - redirect to their default dashboard
  if (!hasPermission) {
    console.log('🚫 RoleBasedRoute: Access denied! Redirecting to default route');
    const defaultRoute = redirectTo || getDefaultRouteForRole(userRole);
    return <Navigate to={defaultRoute} state={{ from: location }} replace />;
  }

  // User has permission - render children
  console.log('✅ RoleBasedRoute: Access granted!');
  return <>{children}</>;
};

export default RoleBasedRoute;
